import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { FormComponent, FormComponentProps } from '..';
import { parseError, uuid } from '../../../services/utils';
import FormField from '../../common/form-field';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import fileCss from './file.module.css';
import css from './image-in-base-64.module.css';

export interface IImageInBase64 {
  id: string;
  src: string;
  name?: string;
}

export default function ImageInBase64(
  name: string,
  label: string = null,
  multiple: boolean = false,
  fileTypes: string = 'jpg,png,jpeg',
  fileMaxSize: number = 1024 * 1024
): FormComponent {
  return ({ ...rest }) => (
    <ImageComponent
      name={name}
      label={label}
      multiple={multiple}
      fileTypes={fileTypes}
      fileMaxSize={fileMaxSize}
      {...rest}
    />
  );
}

export function ImageComponent({
  name,
  label,
  parentName,
  multiple,
  fileTypes,
  fileMaxSize,
  readonly,
  disabled,
  required,
  formValues,
}: FormComponentProps & {
  name: string;
  label: string;
  multiple: boolean;
  fileTypes: string;
  fileMaxSize: number;
  required?: string[];
  formValues?: object;
}) {
  const { t } = useTranslation();
  const {
    register,
    formState: { errors },
    setValue,
    setError,
    trigger,
  } = useFormContext();
  const [images, setImages] = useState<IImageInBase64[]>(null);
  const [invalidImages, setInvalidImages] = useState<IImageInBase64[]>([]);
  const inputName = parentName ? `${parentName}.${name}` : name;
  const error = parseError(inputName, errors);
  const uploadMimeTypes = useMemo(
    () => ['image/jpeg', 'image/pjpeg', 'image/png', 'image/pdf'],
    []
  );

  // Initial images[] setup from formValues
  useEffect(() => {
    if (images !== null) {
      return;
    }

    const values = inputName
      .split('.')
      .reduce((accumulator, currentValue: any) => {
        return accumulator?.hasOwnProperty(currentValue)
          ? accumulator[currentValue]
          : false;
      }, formValues);

    if (values?.length > 0) {
      if (multiple) {
        setImages(values);
      } else {
        setImages([values[0]]);
      }
    } else {
      setImages([]);
    }
  }, [images, inputName, multiple, formValues]);

  // Update and validate input value from images[]
  useEffect(() => {
    if (images === null) {
      return;
    }
    setValue(inputName, images);
    trigger(inputName).then((valid) => {
      if (valid && invalidImages.length > 0) {
        setError(inputName, {
          type: 'manual',
          message: t('form.validate.Size or type error', {
            maxSize: fileMaxSize / 1024 / 1024,
          }),
        });
      }
    });
  }, [
    images,
    inputName,
    setValue,
    setError,
    trigger,
    fileMaxSize,
    invalidImages.length,
    t,
  ]);

  function selectFiles(event: ChangeEvent<HTMLInputElement>) {
    Array.from(event.target.files).forEach((file) => {
      upload(file);
    });
  }

  function getBase64(file: File): Promise<string | ArrayBuffer> {
    return new Promise((resolve, reject) => {
      let reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  async function upload(file: File) {
    // Ignore already added files
    if (images.findIndex((i) => i.name === file.name) !== -1) {
      return;
    }

    if (file.size > fileMaxSize || !uploadMimeTypes.includes(file.type)) {
      setInvalidImages((images) =>
        images.concat({ id: uuid(), src: file.name, name: file.name })
      );
      return;
    }

    const ImageInBase64 = await getBase64(file);
    const image = { id: uuid(), src: ImageInBase64 as string, name: file.name };
    setImages((state) => (multiple ? state.concat(image) : [image]));
  }

  function destroy(image: IImageInBase64) {
    setImages((images) => images.filter((i) => i.id !== image.id));
    setInvalidImages((images) => images.filter((i) => i.id !== image.id));
  }

  function fileList() {
    return (
      images && (
        <ul className={`${css['images']}`}>
          {images.map((image, index) => (
            <li key={`image-in-base-64-${index}`}>
              <img src={image.src} alt="" />
              {!readonly && (
                <div
                  className={fileCss['destroy']}
                  onClick={() => destroy(image)}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </div>
              )}
            </li>
          ))}

          {invalidImages.map((image, index) => (
            <li key={`image-in-base-64-${index}`}>
              {image.src}
              <div
                className={fileCss['destroy']}
                onClick={() => destroy(image)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </div>
            </li>
          ))}
        </ul>
      )
    );
  }

  if (readonly) {
    return (
      <FormField
        label={label ? t(label) : t(name)}
        labelFor={inputName}
        required={required?.includes(inputName)}
        error={error ? error.message : null}
      >
        <input type="hidden" name={inputName} {...register(inputName)} />
        {fileList()}
      </FormField>
    );
  } else {
    return (
      <FormField
        label={label ? t(label) : t(name)}
        labelFor={inputName}
        required={required?.includes(inputName)}
        error={error ? error.message : null}
      >
        <input type="hidden" name={inputName} {...register(inputName)} />
        <label className={fileCss['input-container']}>
          <input
            type={'file'}
            multiple={multiple}
            onChange={selectFiles}
            accept={fileTypes}
            readOnly={readonly}
            disabled={disabled}
          />
          <div>
            <div className={fileCss['upload']}>
              <span>{t('upload')}</span>
            </div>
          </div>

          <div className={fileCss['help']}>
            <span>
              <Trans
                t={t}
                i18nKey="pages.internal.system-configuration.Upload restrictions"
                components={{ 1: <strong /> }}
                values={{
                  fileTypes: fileTypes.split(',').join(', '),
                  maxSize: fileMaxSize / 1024 / 1024,
                }}
              />
            </span>
          </div>
        </label>
        {fileList()}
      </FormField>
    );
  }
}
